import React, { useMemo, useState, useCallback, useEffect } from "react";

const ReferenceAgentSelector = ({
    formData,
    sectionKey,
    agentKey,
    currentAgentId,
    selectedRefs,
    onChange,
    persistedExpanded,
    onExpandedChange,
    sectionLabel = "Section"
}) => {

    const { sectionsDict = {}, sectionOrder = [] } = formData || {};

    const [expandedSections, setExpandedSectionsLocal] = useState(
        persistedExpanded && Object.keys(persistedExpanded).length > 0
            ? persistedExpanded
            : {}
    );

    // Wrapper: update local state + notify parent for persistence
    const setExpandedSections = (updater) => {
        setExpandedSectionsLocal(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            if (onExpandedChange) onExpandedChange(next);
            return next;
        });
    };

    // Queue/BFS dependency walk to block transitive circular references.
    const dependentAgents = useMemo(() => {
        const deps = new Set();
        if (!currentAgentId) return deps;

        const queue = [currentAgentId];

        while (queue.length > 0) {
            const curr = queue.shift();

            sectionOrder.forEach(sk => {
                const sec = sectionsDict[sk];
                if (!sec || sec.isDeleted) return;

                (sec.agentOrder || []).forEach(ak => {
                    const ag = sec.agentsDict?.[ak];
                    if (!ag || ag.isDeleted) return;
                    const agIdentifier = ag.id || ag.temp_id;
                    if (!agIdentifier) return;
                    const refs = ag.reference_agent_ids || [];
                    if (refs.includes(curr) && !deps.has(agIdentifier)) {
                        deps.add(agIdentifier);
                        queue.push(agIdentifier);
                    }
                });
            });
        }

        return deps;
    }, [sectionsDict, sectionOrder, currentAgentId]);

    // Build tree structure
    const treeRoots = useMemo(() => {
        const nodes = {};
        const roots = [];

        const parseOrderingId = (id) =>
            id.split(".").map(n => Number(n));

        // Sort sections: summary sections (S-prefix) first, then numeric ordering
        const sortedSectionOrder = [...sectionOrder].sort((a, b) => {
            const secA = sectionsDict[a];
            const secB = sectionsDict[b];
            const aIsSummary = secA?.is_summary_section;
            const bIsSummary = secB?.is_summary_section;

            // Summary sections always come first
            if (aIsSummary && !bIsSummary) return -1;
            if (!aIsSummary && bIsSummary) return 1;
            if (aIsSummary && bIsSummary) {
                return (secA.section_ordering_id || '').localeCompare(secB.section_ordering_id || '');
            }

            const A = parseOrderingId(secA.section_ordering_id);
            const B = parseOrderingId(secB.section_ordering_id);

            const len = Math.max(A.length, B.length);
            for (let i = 0; i < len; i++) {
                const x = A[i] || 0;
                const y = B[i] || 0;
                if (x !== y) return x - y;
            }
            return 0;
        });

        // Build nodes
        sortedSectionOrder.forEach(sk => {
            const sec = sectionsDict[sk];
            if (!sec || sec.isDeleted) return;

            nodes[sk] = {
                key: sk,
                orderingId: sec.section_ordering_id,
                name: sec.section_name,
                agentOrder: sec.agentOrder || [],
                agents: sec.agentsDict || {},
                children: []
            };
        });

        // Link parent–child relationships
        sortedSectionOrder.forEach(sk => {
            const sec = sectionsDict[sk];
            if (!sec || sec.isDeleted) return;

            const order = sec.section_ordering_id;
            const dot = order.lastIndexOf(".");

            if (dot === -1) {
                roots.push(nodes[sk]);
            } else {
                const parentId = order.substring(0, dot);
                const parent = Object.values(nodes).find(
                    n => n.orderingId === parentId
                );
                if (parent) parent.children.push(nodes[sk]);
                else roots.push(nodes[sk]);
            }
        });

        return roots;
    }, [sectionsDict, sectionOrder]);

    useEffect(() => {
        if (treeRoots.length === 0) return;

        const defaults = {};

        const collectDefaults = (node) => {
            defaults[node.key] = false;
            node.children?.forEach(child => collectDefaults(child));
        };

        treeRoots.forEach(root => collectDefaults(root));

        // Only set defaults for keys not already in state (preserves persisted expand/collapse)
        setExpandedSections(prev => {
            const merged = { ...defaults };
            Object.keys(prev).forEach(key => {
                if (key in merged) {
                    merged[key] = prev[key];
                }
            });
            return merged;
        });
    }, [treeRoots]);

    // Toggle section expand
    const toggleSection = (key) => {
        setExpandedSections(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const areAllExpanded = () => {
        return treeRoots.length > 0 && treeRoots.every(root => {
            const check = (node) => expandedSections[node.key] && node.children.every(c => check(c));
            return check(root);
        });
    };

    const handleToggleExpandCollapseAll = () => {
        const shouldExpand = !areAllExpanded();
        const allKeys = {};
        const markAll = (node) => {
            allKeys[node.key] = shouldExpand;
            node.children.forEach(child => markAll(child));
        };
        treeRoots.forEach(root => markAll(root));
        setExpandedSections(prev => ({ ...prev, ...allKeys }));
    };

    const collectAllAgentIds = (node) => {
        let ids = [];

        // current section agents
        node.agentOrder.forEach(agKey => {
            const ag = node.agents[agKey];
            if (ag && !ag.isDeleted) {
                ids.push(ag.id || ag.temp_id);
            }
        });

        // recursively include children’s agents
        node.children.forEach(child => {
            ids = ids.concat(collectAllAgentIds(child));
        });

        return ids;
    };

    // Section-level toggle
    const toggleSectionSelect = (sectionNode, checked) => {
        const allAgentIds = collectAllAgentIds(sectionNode);
        const selectableIds = allAgentIds.filter(id => id !== currentAgentId && !dependentAgents.has(id));

        let updated = [...selectedRefs];

        if (checked) {
            selectableIds.forEach(id => {
                if (!updated.includes(id)) {
                    updated.push(id);
                }
            });
        } else {
            updated = updated.filter(id => !selectableIds.includes(id));
        }

        onChange(updated);
    };

    const getSectionSelectionState = (sectionNode) => {
        const agentIds = collectAllAgentIds(sectionNode);
        const selectableIds = agentIds.filter(id => id !== currentAgentId && !dependentAgents.has(id));

        const selectedCount = selectableIds.filter(id => selectedRefs.includes(id)).length;

        return {
            allSelected: selectedCount === selectableIds.length && selectableIds.length > 0,
            partial: selectedCount > 0 && selectedCount < selectableIds.length
        };
    };

    const toggleAgent = useCallback((agentId) => {
        let updated = [...selectedRefs];
        if (updated.includes(agentId)) {
            updated = updated.filter(x => x !== agentId);
        } else {
            updated.push(agentId);
        }
        onChange(updated);
    }, [selectedRefs, onChange]);

    // Render agent row
    const renderAgents = (sectionNode, indent) => {
        return sectionNode.agentOrder.map((agKey) => {
            const ag = sectionNode.agents[agKey];
            if (!ag || ag.isDeleted) return null;

            const agId = ag.id || ag.temp_id;
            const isSelf = agId === currentAgentId;
            const isDependent = dependentAgents.has(agId);
            const isDisabled = isSelf || isDependent;

            return (
                <div
                    key={agKey}
                    className="ms-4"
                    style={{ paddingLeft: `${indent + 10}px` }}
                >
                    <span className="ev-message-card1 d-inline-flex align-items-center pe-2 mb-1">

                        <input
                            type="checkbox"
                            className="form-check-input me-2 mt-0"
                            disabled={isDisabled}
                            checked={selectedRefs.includes(agId)}
                            onChange={() => !isDisabled && toggleAgent(agId)}
                            style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                        />

                        <span
                            className={`reference-agent-label ev-small-p-blue ${isDisabled ? "text-muted" : ""
                                }`}
                            style={{ whiteSpace: "nowrap" }}
                        >
                            {`S.${sectionNode.orderingId}. Agent ${ag.agent_ordering_id} - ${ag.agent_name}`}
                            {isSelf && " (current)"}
                            {isDependent && " (Already refer to this agent)"}
                        </span>
                    </span>
                </div>
            );
        });
    };

    const renderSection = (node, indent = 0) => {
        const { allSelected, partial } = getSectionSelectionState(node);

        return (
            <div key={node.key} className="ev-card-3 ev-padding-small mb-1">

                {/* Section Header */}
                <div className="d-flex align-items-center">

                    <button
                        className="btn btn-sm p-0 me-2 w-3"
                        onClick={() => toggleSection(node.key)}
                    >
                        <i
                            className={`ev-reg-p-dark bi ${expandedSections[node.key] ? "bi-chevron-down" : "bi-chevron-right"
                                }`}
                        ></i>
                    </button>

                    <input
                        type="checkbox"
                        className="form-check-input me-2 position-relative mt-0"
                        checked={allSelected}
                        ref={el => {
                            if (el) el.indeterminate = partial;
                        }}
                        onChange={(e) => toggleSectionSelect(node, e.target.checked)}
                    />

                    <span className="ev-reg-p-blue">
                        {sectionLabel} {node.orderingId}: {node.name}
                    </span>
                </div>

                {/* Agents + Child Sections */}
                {expandedSections[node.key] && (
                    <div className="mt-1 ms-2 ps-2">
                        {renderAgents(node, indent)}

                        {node.children.map(child =>
                            renderSection(child, indent + 16)
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div>
            <div className="ev-alert-card-warning mt-2 mb-2">
                <p className="mb-0">
                    <i className="bi bi-info-circle me-1"></i>
                    &nbsp;
                    Select agents whose outputs will be passed as context.
                </p>
            </div>

           <div className="d-flex justify-content-end mb-2">
                <button
                    type="button"
                    className="ev-btn-light-fill ev-bold-reg avatar-tooltip ev-btn-small border"
                    onClick={handleToggleExpandCollapseAll}
                    disabled={treeRoots.length === 0}
                    data-tooltip={areAllExpanded() ? "Collapse all sections" : "Expand all sections"}
                >
                    <i className={`bi ${areAllExpanded() ? "bi-arrows-collapse" : "bi-arrows-expand"} me-1`}></i>
                    {areAllExpanded() ? "Collapse All" : "Expand All"}
                </button>
            </div>

            <div className="scrollable-element vh-45">
                {treeRoots.length === 0 ? (
                    <p className="ev-small-p-blue">No agents found</p>
                ) : (
                    treeRoots.map(root => renderSection(root))
                )}
            </div>
        </div>
    );
};

export default ReferenceAgentSelector;
